/**
 * Temporary stand-in for a BE validation of `validation_regex`, which BE wraps into `/<value>/` and runs with PCRE.
 * Delete it together with its usages once BE validates the pattern with the real engine.
 *
 * It is not a PCRE parser: it walks the pattern once, reports structural mistakes any engine rejects and marks the
 * constructs PCRE has but the browser (JS) engine lacks or treats differently, so a preview in the browser can't be
 * trusted for them.
 */
export type PcreLintError =
    | "trailing_backslash"
    | "unterminated_class"
    | "unbalanced_group"
    | "nothing_to_repeat"
    | "bad_quantifier"
    | "bad_property";

export interface PcreLintResult {
    errors: PcreLintError[];
    /** Uses syntax that PCRE has and JS lacks or reads differently (named group `(?P<n>`, atomic group, `\A`, ...) */
    pcreOnly: boolean;
}

/** Escapes that exist in PCRE, but in JS mean something else or nothing */
const PCRE_ONLY_ESCAPES = new Set([
    "A",
    "Z",
    "z",
    "G",
    "K",
    "R",
    "h",
    "H",
    "v",
    "V",
    "X",
    "N",
    "Q",
    "E",
    "p",
    "P",
    "g",
]);

/** Group openers `(?…` the browser engine understands the same way */
const JS_GROUP_PREFIXES = ["?:", "?=", "?!", "?<=", "?<!"];

const QUANTIFIER = /^\{(\d+)(?:,(\d*))?\}/;

export const lintPcre = (pattern: string): PcreLintResult => {
    const errors = new Set<PcreLintError>();
    let pcreOnly = false;
    let groupDepth = 0;
    // Whether the previous token can be repeated, `*` after `(`, `|` or at the start has nothing to repeat
    let canRepeat = false;
    let i = 0;

    while (i < pattern.length) {
        const char = pattern[i];

        if (char === "\\") {
            const next = pattern[i + 1];
            if (next === undefined) {
                errors.add("trailing_backslash");
                break;
            }
            if (next === "Q") {
                // \Q...\E is a literal run
                pcreOnly = true;
                const end = pattern.indexOf("\\E", i + 2);
                i = end === -1 ? pattern.length : end + 2;
                canRepeat = true;
                continue;
            }
            if (PCRE_ONLY_ESCAPES.has(next)) {
                pcreOnly = true;
            }
            if (next === "p" || next === "P") {
                // `\pL` (one letter) or `\p{Name}`, the property name must not be empty
                if (pattern[i + 2] === "{") {
                    const end = pattern.indexOf("}", i + 3);
                    if (end === -1 || end === i + 3) {
                        errors.add("bad_property");
                    }
                    i = end === -1 ? pattern.length : end + 1;
                } else {
                    if (!/[A-Za-z]/.test(pattern[i + 2] ?? "")) {
                        errors.add("bad_property");
                    }
                    i += 3;
                }
                canRepeat = true;
                continue;
            }
            canRepeat = !["A", "Z", "z", "G", "b", "B"].includes(next);
            i += 2;
            continue;
        }

        if (char === "[") {
            let j = i + 1;
            if (pattern[j] === "^") {
                j++;
            }
            // A `]` right after the opening is a literal
            if (pattern[j] === "]") {
                j++;
            }
            let closed = false;
            while (j < pattern.length) {
                if (pattern[j] === "\\") {
                    if (PCRE_ONLY_ESCAPES.has(pattern[j + 1])) {
                        pcreOnly = true;
                    }
                    j += 2;
                    continue;
                }
                if (pattern[j] === "[" && /^\[[:.=]/.test(pattern.slice(j, j + 2))) {
                    // POSIX class like [:alpha:]
                    const end = pattern.indexOf(pattern[j + 1] + "]", j + 2);
                    if (end !== -1) {
                        pcreOnly = true;
                        j = end + 2;
                        continue;
                    }
                }
                if (pattern[j] === "]") {
                    closed = true;
                    break;
                }
                j++;
            }
            if (!closed) {
                errors.add("unterminated_class");
                break;
            }
            canRepeat = true;
            i = j + 1;
            continue;
        }

        if (char === "(") {
            groupDepth++;
            canRepeat = false;
            if (pattern[i + 1] === "?") {
                const rest = pattern.slice(i + 1);
                if (rest.startsWith("?#")) {
                    // Comment group, skipped up to the closing bracket
                    const end = pattern.indexOf(")", i);
                    if (end === -1) {
                        errors.add("unbalanced_group");
                        break;
                    }
                    groupDepth--;
                    i = end + 1;
                    continue;
                }
                if (rest.startsWith("?(")) {
                    // Conditional group `(?(cond)yes|no)`: the condition is scanned without closing the group itself
                    pcreOnly = true;
                    if (rest.startsWith("?(?")) {
                        // The condition is an assertion, its own group is scanned as any other one
                        i += 2;
                    } else {
                        const end = pattern.indexOf(")", i + 2);
                        if (end === -1) {
                            errors.add("unbalanced_group");
                            break;
                        }
                        i = end + 1;
                    }
                    continue;
                }
                const jsPrefix = JS_GROUP_PREFIXES.find((prefix) => rest.startsWith(prefix));
                const named = /^\?<[A-Za-z_]\w*>/.exec(rest);
                if (jsPrefix) {
                    i += 1 + jsPrefix.length;
                    continue;
                }
                if (named) {
                    i += 1 + named[0].length;
                    continue;
                }
                pcreOnly = true;
                // `(?P<name>`, `(?'name'`, `(?>`, `(?|`: the opener is consumed, the group continues
                const opener = /^\?(?:P?<[A-Za-z_]\w*>|'[A-Za-z_]\w*'|>|\||[a-zA-Z^-]*:)/.exec(rest);
                if (opener) {
                    i += 1 + opener[0].length;
                    continue;
                }
                // `(?i)`, `(?R)`, `(?1)`, `(?&name)`, `(?P=name)`, `(?(cond)…)`: a complete construct up to `)`
                const end = pattern.indexOf(")", i);
                if (end === -1) {
                    errors.add("unbalanced_group");
                    break;
                }
                groupDepth--;
                canRepeat = true;
                i = end + 1;
                continue;
            }
            i++;
            continue;
        }

        if (char === ")") {
            if (groupDepth === 0) {
                errors.add("unbalanced_group");
            } else {
                groupDepth--;
            }
            canRepeat = true;
            i++;
            continue;
        }

        if (char === "|") {
            canRepeat = false;
            i++;
            continue;
        }

        const isQuantifierChar = char === "*" || char === "+" || char === "?";
        const braces = char === "{" ? QUANTIFIER.exec(pattern.slice(i)) : null;
        if (isQuantifierChar || braces) {
            if (!canRepeat) {
                errors.add("nothing_to_repeat");
            }
            let length = 1;
            if (braces) {
                length = braces[0].length;
                if (braces[2] && Number(braces[1]) > Number(braces[2])) {
                    errors.add("bad_quantifier");
                }
            }
            i += length;
            // `?` makes it lazy, `+` makes it possessive (PCRE only)
            if (pattern[i] === "?") {
                i++;
            } else if (pattern[i] === "+") {
                pcreOnly = true;
                i++;
            }
            // A quantifier can't be quantified again (`a**`, `a{2}*`), only made lazy or possessive
            canRepeat = false;
            continue;
        }

        canRepeat = char !== "^" && char !== "$";
        i++;
    }

    if (groupDepth > 0) {
        errors.add("unbalanced_group");
    }

    return { errors: [...errors], pcreOnly };
};
